import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Loader from "../Components/Loader";
import PageTitle from "./include/PageTitle";
import {
  AREAS,
  LISTING_FILTER,
  LISTING_OPTIONS,
  PRICE_DEFAULT,
  PROPERTY_TYPE_OPTIONS,
  SALE_MIN_PRICE_OPTION,
  filtersFromSearchParams,
  priceOptionsForListing,
  propertyMatchesFilters,
} from "../constants/propertyFilters";
import { pickLocalizedValue, useFilterLabels, useI18n } from "../i18n/I18nContext";
import { API_BASE, API_BASES, fetchApiJson } from "../config/apiBase";
import { formatPropertyPrice, getPropertyPriceSuffix } from "../Utils/propertyPricing";
import { lightenPropertyList } from "../Utils/propertyList";
import Seo, { ARABIC_SEO_KEYWORDS, mergeSeoKeywords } from "../Components/Seo";

const fallbackPropertyImage = "/img/property-placeholder.svg";
const resolvePropertyImage = (url) => {
  if (!url) return fallbackPropertyImage;
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/(uploads|api)\//i.test(url)) {
    return `${API_BASE}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/") || url.startsWith("/api/")) return `${API_BASE}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const getPropertyStatusLabel = (property, fl) => {
  const status = String(property?.status || "").toUpperCase();
  if (status === "SOLD") return "Sold";
  if (status === "RENTED") return "Rented";
  const isRent = String(property?.listingType || "").toUpperCase() === "RENT";
  return isRent ? fl.listing("Rent") : fl.listing("Sale");
};

const SORT_OPTIONS = [
  "Default sorting",
  "Sort by popularity",
  "Sort by new arrivals",
  "Sort by price: low to high",
  "Sort by price: high to low",
];

const Property = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("Default sorting");
  const [currentPage, setCurrentPage] = useState(1);
  const [shareOpenId, setShareOpenId] = useState(null);
  const shareMenuRef = useRef(null);
  const itemsPerPage = 6;
  const { t, locale } = useI18n();
  const fl = useFilterLabels();

  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

  const setUrlParam = useCallback(
    (key, value, treatAsEmpty) => {
      const p = new URLSearchParams(searchParams);
      const empty = treatAsEmpty ?? [ "", PRICE_DEFAULT, LISTING_FILTER.ALL, null, undefined ];
      if (empty.includes(value)) p.delete(key);
      else p.set(key, String(value));
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearUrlFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
    setSearch("");
  }, [setSearchParams]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "property_listing_view",
      page_type: "property_listing",
      content_name: "Property Page",
      content_category: "Real Estate",
      locale,
      filters: {
        area: filters.area || "all",
        listing: filters.listing || LISTING_FILTER.ALL,
        type: filters.type || "all",
        price: filters.price || PRICE_DEFAULT,
        search: search.trim() || filters.q || "",
      },
    });
  }, [filters.area, filters.listing, filters.price, filters.q, filters.type, locale, search]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        setError("");

        const { json, base } = await fetchApiJson(`/api/properties?lang=${locale}`);
        const list = lightenPropertyList(
          (Array.isArray(json) ? json : json.data || []).filter(
            (property) => property?.isActive !== false && String(property?.status || "").toUpperCase() !== "INACTIVE"
          ),
          base || API_BASE
        );
        setProperties(list);
      } catch (err) {
        setError(
          err.message ||
            `Could not load properties from any configured API base: ${API_BASES.join(", ")}`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [locale]);

  const filteredProperties = useMemo(() => {
    let list = [...properties];
    const combined = { ...filters, q: search.trim() || filters.q || "" };
    list = list.filter((p) => propertyMatchesFilters(p, combined));

    if (sortBy === "Sort by price: low to high") {
      list.sort((a, b) => (a.price == null ? Number.POSITIVE_INFINITY : a.price) - (b.price == null ? Number.POSITIVE_INFINITY : b.price));
    } else if (sortBy === "Sort by price: high to low") {
      list.sort((a, b) => (b.price == null ? Number.NEGATIVE_INFINITY : b.price) - (a.price == null ? Number.NEGATIVE_INFINITY : a.price));
    } else if (sortBy === "Sort by new arrivals") {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === "Sort by popularity") {
      list.sort((a, b) => (b.views || b.popularity || 0) - (a.views || a.popularity || 0));
    }

    return list;
  }, [properties, filters, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProperties = filteredProperties.slice(startIndex, startIndex + itemsPerPage);

  const showingLabel = useMemo(
    () =>
      t("property.showing")
        .replace("{shown}", String(paginatedProperties.length))
        .replace("{total}", String(filteredProperties.length)),
    [t, paginatedProperties.length, filteredProperties.length]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, searchParams]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!shareOpenId) return;
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShareOpenId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [shareOpenId]);

  const getShareLinks = (property) => {
    const propertyUrl = `${window.location.origin}/property/${property.id}`;
    const shareText = `${pickLocalizedValue(property, "title", locale, "Property")} - ${propertyUrl}`;

    return {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(propertyUrl)}`,
      instagram: "https://www.instagram.com/",
    };
  };

  const handleInstagramShare = async (event, property) => {
    event.preventDefault();
    const propertyUrl = `${window.location.origin}/property/${property.id}`;

    try {
      await navigator.clipboard.writeText(propertyUrl);
      alert(t("property.shareCopyOk"));
    } catch (err) {
      alert(t("property.shareCopyFail"));
    }

    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    setShareOpenId(null);
  };

  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    if (!p.get("listing") && p.get("price")) {
      p.delete("price");
      setSearchParams(p, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const priceOptionList = useMemo(
    () =>
      priceOptionsForListing(
        filters.listing === LISTING_FILTER.ALL ? LISTING_FILTER.SALE : filters.listing
      ),
    [filters.listing]
  );

  const filterSidebar = useMemo(
    () => (
      <>
        <div className="ltn__car-dealer-form-box qtech-car-filter-row qtech-property-filters-vertical">
          <div className="ltn__car-dealer-form-item">
            <select
              className="qtech-native-select"
              value={filters.area}
              onChange={(e) => setUrlParam("area", e.target.value, [""])}
            >
              {AREAS.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {fl.area(opt.value)}
                </option>
              ))}
            </select>
          </div>
          <div className="ltn__car-dealer-form-item">
            <select
              className="qtech-native-select"
              value={filters.listing}
              onChange={(e) => {
                const v = e.target.value;
                const p = new URLSearchParams(searchParams);
                if (!v || v === LISTING_FILTER.ALL) p.delete("listing");
                else p.set("listing", v);
                if (v === LISTING_FILTER.SALE) p.set("price", SALE_MIN_PRICE_OPTION);
                else p.delete("price");
                setSearchParams(p, { replace: true });
              }}
            >
              {LISTING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {fl.listing(opt.value)}
                </option>
              ))}
            </select>
          </div>
          <div className="ltn__car-dealer-form-item">
            <select
              className="qtech-native-select"
              value={filters.type}
              onChange={(e) => setUrlParam("type", e.target.value, [""])}
            >
              {PROPERTY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {fl.type(opt.value)}
                </option>
              ))}
            </select>
          </div>
          <div className="ltn__car-dealer-form-item">
            <select
              key={filters.listing}
              className="qtech-native-select"
              value={priceOptionList.includes(filters.price) ? filters.price : PRICE_DEFAULT}
              onChange={(e) => setUrlParam("price", e.target.value, ["", PRICE_DEFAULT])}
            >
              {priceOptionList.map((opt) => (
                <option key={opt} value={opt}>
                  {fl.price(opt)}
                </option>
              ))}
            </select>
          </div>
          <div className="ltn__car-dealer-form-item">
            <input
              type="text"
              className="qtech-filter-keyword"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("filters.keywordPlaceholder")}
            />
          </div>
          <div className="ltn__car-dealer-form-item">
            <button type="button" className="btn theme-btn-1 btn-effect-1 text-uppercase w-100" onClick={clearUrlFilters}>
              {t("filters.resetFilters")}
            </button>
          </div>
        </div>
      </>
    ),
    [filters, search, fl, t, priceOptionList, searchParams, setUrlParam, setSearchParams, clearUrlFilters]
  );

  return (
    <div className="qtech-properties-page">
      <Seo
        title="Properties for Sale and Rent in Saudi Arabia and Al Khobar | QTECH"
        titleAr="عقارات للبيع والإيجار في الخبر والسعودية | شركة تقنيات الجودة"
        description="Browse QTECH property listings in Saudi Arabia and Al Khobar, including residential and commercial properties available for sale and rent."
        descriptionAr="تصفح عقارات شركة تقنيات الجودة في السعودية والخبر، بما في ذلك شقق للبيع، شقق للإيجار، فلل للبيع، وحدات للبيع، وفرص استثمار عقاري."
        keywords={mergeSeoKeywords(
          "properties Saudi Arabia, properties for sale Saudi Arabia, properties for rent Saudi Arabia, real estate listings, QTECH properties",
          "investment properties Saudi Arabia, real estate investment properties, buy investment property Al Khobar, Saudi Arabia investment apartments, real estate investment opportunities Saudi Arabia"
        )}
        keywordsAr={mergeSeoKeywords(
          ARABIC_SEO_KEYWORDS.property,
          "عقارات استثمارية, عقارات استثمارية في السعودية, عقارات استثمارية في الخبر, فرص استثمار عقاري, شراء عقار استثماري, شقق استثمارية, استثمار عقاري في الخبر"
        )}
        path="/property"
        image="/img/property-placeholder.svg"
      />
  
      <PageTitle
        titleKey="pages.properties.title"
        currentPageKey="pages.properties.title"
        bgImage="/img/bg/14.jpg"
        endContent={filterSidebar}
      />
  
      <div className="ltn__product-area ltn__product-gutter mb-100">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ltn__shop-options">
                <ul>
                  <li>
                    <div className="ltn__grid-list-tab-menu ">
                      <div className="nav">
                        <a className="active show" data-bs-toggle="tab" href="#liton_product_grid">
                          <i className="fas fa-th-large" />
                        </a>
                        <a data-bs-toggle="tab" href="#liton_product_list">
                          <i className="fas fa-list" />
                        </a>
                      </div>
                    </div>
                  </li>
  
                  <li>
  <div className="short-by text-center">
    <select
      className="nice-select"
      style={{ display: "none" }}
      value={sortBy}
      onChange={(e) => setSortBy(e.target.value)}
    >
      {SORT_OPTIONS.map((item) => (
        <option key={item} value={item}>
          {fl.sort(item)}
        </option>
      ))}
    </select>

    <div className="nice-select" tabIndex={0}>
      <span className="current">{fl.sort(sortBy)}</span>
      <ul className="list">
        {SORT_OPTIONS.map((item) => (
          <li
            key={item}
            data-value={item}
            className={`option ${sortBy === item ? "selected" : ""}`}
            onClick={() => setSortBy(item)}
          >
            {fl.sort(item)}
          </li>
        ))}
      </ul>
    </div>
  </div>
</li>
  
                  <li>
                    <div className="showing-product-number text-right">
                      <span>{showingLabel}</span>
                    </div>
                  </li>
                </ul>
              </div>
  
              <div className="tab-content">
                {/* GRID VIEW */}
                <div className="tab-pane fade active show" id="liton_product_grid">
                  <div className="ltn__product-tab-content-inner ltn__product-grid-view">
                    <div className="row">
                      {loading && (
                        <div className="col-12 text-center mb-30">
                          <Loader label={t("property.loading")} />
                        </div>
                      )}
  
                      {!loading && error && (
                        <div className="col-12 text-center mb-30">
                          <p className="text-danger">{error}</p>
                        </div>
                      )}
  
                      {!loading && !error && filteredProperties.length === 0 && (
                        <div className="col-12 text-center mb-30">
                          <p>{t("property.noResults")}</p>
                        </div>
                      )}
  
                      {!loading &&
                        !error &&
                        paginatedProperties.map((property) => {
                          const statusLabel = getPropertyStatusLabel(property, fl);
                          const image = property.coverImage || property.image || fallbackPropertyImage;
                          const agentAvatar =
                            property.createdBy?.agentProfile?.profileImageUrl ||
                            property.createdBy?.profileImageUrl ||
                            property.agentImage ||
                            "";
                          const agentName = property.createdBy?.fullName || "QTECH Admin";
                          const agentRole = property.createdBy?.agentProfile?.companyName || "Property Consultant";
                          const location =
                            [pickLocalizedValue(property, "city", locale, property.city), pickLocalizedValue(property, "area", locale, property.area)]
                              .filter(Boolean)
                              .join(", ") ||
                            property.location ||
                            [property.address, property.city].filter(Boolean).join(", ") ||
                            t("property.locationNotSet");
  
                          return (
                            <div className="col-lg-4 col-sm-6 col-12" key={property.id}>
                              <div className="ltn__product-item ltn__product-item-4 ltn__product-item-5 text-center---">
                                <div className="product-img">
                                  <Link to={`/property/${property.id}`}>
                          <img className="qtech-property-card-image" src={image} alt={pickLocalizedValue(property, "title", locale, "Property")} />
                                  </Link>
                                </div>
  
                                <div className="product-info">
                                  <div className="product-badge">
                                    <ul>
                                      <li className="sale-badg">{statusLabel}</li>
                                    </ul>
                                  </div>
  
                                  <h2 className="product-title">
                                    <Link
                                      to={`/property/${property.id}`}
                                      title={pickLocalizedValue(property, "title", locale, "New Apartment Nice View")}
                                    >
                                      {pickLocalizedValue(property, "title", locale, "New Apartment Nice View")}
                                    </Link>
                                  </h2>
  
                                  <div className="product-img-location">
                                    <ul>
                                      <li>
                                        <a href="locations.html">
                                          <i className="flaticon-pin" /> {location}
                                        </a>
                                      </li>
                                    </ul>
                                  </div>
  
                                  <ul className="ltn__list-item-2 ltn__list-item-2-before qtech-property-card-meta">
                                    <li>
                                      <span>{property.bedrooms ?? 0} <i className="flaticon-bed" /></span>
                                      {t("property.bedrooms")}
                                    </li>
                                    <li>
                                      <span>{property.bathrooms ?? 0} <i className="flaticon-clean" /></span>
                                      {t("property.bathrooms")}
                                    </li>
                                    {property.areaSqft !== null && property.areaSqft !== undefined ? (
                                      <li>
                                        <span>
                                          {property.areaSqft} <i className="flaticon-square-shape-design-interface-tool-symbol" />
                                        </span>
                                        {t("property.squareFt")}
                                      </li>
                                    ) : null}
                                  </ul>
  
                                  <div className="product-hover-action">
                                    <ul>
                                      <li ref={shareOpenId === `grid-${property.id}` ? shareMenuRef : null}>
                                        <a
                                          href="#"
                                          title="Share"
                                          onClick={(event) => {
                                            event.preventDefault();
                                            setShareOpenId(shareOpenId === `grid-${property.id}` ? null : `grid-${property.id}`);
                                          }}
                                        >
                                          <i className="fas fa-share-alt" />
                                        </a>
                                        {shareOpenId === `grid-${property.id}` && (
                                          <div
                                            className="ltn__social-media-2"
                                            style={{
                                              position: "absolute",
                                              zIndex: 10,
                                              marginTop: "8px",
                                              background: "#fff",
                                              boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                                              borderRadius: "6px",
                                              padding: "8px 12px",
                                            }}
                                          >
                                            <ul style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                              <li>
                                                <a href={getShareLinks(property).whatsapp} target="_blank" rel="noreferrer" title="Share on WhatsApp">
                                                  <i className="fab fa-whatsapp" />
                                                </a>
                                              </li>
                                              <li>
                                                <a href={getShareLinks(property).facebook} target="_blank" rel="noreferrer" title="Share on Facebook">
                                                  <i className="fab fa-facebook-f" />
                                                </a>
                                              </li>
                                              <li>
                                                <a href={getShareLinks(property).instagram} onClick={(event) => handleInstagramShare(event, property)} title="Share on Instagram">
                                                  <i className="fab fa-instagram" />
                                                </a>
                                              </li>
                                            </ul>
                                          </div>
                                        )}
                                      </li>
                                      <li>
                                        <a
                                          href="#"
                                          title="Wishlist"
                                          data-bs-toggle="modal"
                                          data-bs-target="#liton_wishlist_modal"
                                        >
                                          <i className="flaticon-heart-1" />
                                        </a>
                                      </li>
                                      <li>
                                        <Link to={`/property/${property.id}`} title="Product Details">
                                          <i className="flaticon-add" />
                                        </Link>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
  
                                <div className="product-info-bottom">
                                  {agentAvatar ? (
                                    <div className="real-estate-agent">
                                      <div className="agent-img">
                                        <Link to={`/property/${property.id}`}>
                                          <img src={resolvePropertyImage(agentAvatar)} alt={agentName} />
                                        </Link>
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="product-price">
                                    {(() => {
                                      const priceInfo = formatPropertyPrice(property, {
                                        negotiationText: t("property.clickForNegotiation"),
                                      });
                                      return priceInfo.isNegotiation ? (
                                        <a href={priceInfo.href} target="_blank" rel="noreferrer">
                                          {priceInfo.text}
                                        </a>
                                      ) : (
                                        <span>
                                          {priceInfo.text || "—"} {priceInfo.text ? priceInfo.suffix : ""}
                                          {priceInfo.text ? <label>{` ${getPropertyPriceSuffix(property, t, priceInfo.suffix)}`}</label> : null}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
  
                {/* LIST VIEW */}
                <div className="tab-pane fade" id="liton_product_list">
                  <div className="ltn__product-tab-content-inner ltn__product-list-view">
                    <div className="row">
                      {!loading &&
                        !error &&
                        paginatedProperties.map((property) => {
                          const statusLabel = getPropertyStatusLabel(property, fl);
                          const image = property.coverImage || property.image || fallbackPropertyImage;
                          const agentAvatar =
                            property.createdBy?.agentProfile?.profileImageUrl ||
                            property.createdBy?.profileImageUrl ||
                            property.agentImage ||
                            "";
                          const agentName = property.createdBy?.fullName || "QTECH Admin";
                          const agentRole = property.createdBy?.agentProfile?.companyName || "Property Consultant";
                          const location =
                            [pickLocalizedValue(property, "city", locale, property.city), pickLocalizedValue(property, "area", locale, property.area)]
                              .filter(Boolean)
                              .join(", ") ||
                            property.location ||
                            [property.address, property.city].filter(Boolean).join(", ") ||
                            t("property.locationNotSet");
  
                          return (
                            <div className="col-lg-12" key={`list-${property.id}`}>
                              <div className="ltn__product-item ltn__product-item-4 ltn__product-item-5">
                                <div className="product-img">
                                  <Link to={`/property/${property.id}`}>
                            <img className="qtech-property-card-image" src={image} alt={pickLocalizedValue(property, "title", locale, "Property")} />
                                  </Link>
                                </div>
  
                                <div className="product-info">
                                  <div className="product-badge-price">
                                    <div className="product-badge">
                                      <ul>
                                        <li className="sale-badg">{statusLabel}</li>
                                      </ul>
                                    </div>
                                    <div className="product-price">
                                      {(() => {
                                        const priceInfo = formatPropertyPrice(property, {
                                          negotiationText: t("property.clickForNegotiation"),
                                        });
                                        return priceInfo.isNegotiation ? (
                                          <a href={priceInfo.href} target="_blank" rel="noreferrer">
                                            {priceInfo.text}
                                          </a>
                                        ) : (
                                          <span>
                                            {priceInfo.text || "—"} {priceInfo.text ? priceInfo.suffix : ""}
                                            {priceInfo.text ? <label>{` ${getPropertyPriceSuffix(property, t, priceInfo.suffix)}`}</label> : null}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  </div>
  
                                  <h2 className="product-title">
                                    <Link
                                      to={`/property/${property.id}`}
                                      title={pickLocalizedValue(property, "title", locale, "New Apartment Nice View")}
                                    >
                                      {pickLocalizedValue(property, "title", locale, "New Apartment Nice View")}
                                    </Link>
                                  </h2>
  
                                  <div className="product-img-location">
                                    <ul>
                                      <li>
                                        <a href="locations.html">
                                          <i className="flaticon-pin" /> {location}
                                        </a>
                                      </li>
                                    </ul>
                                  </div>
  
                                  <ul className="ltn__list-item-2 ltn__list-item-2-before qtech-property-card-meta">
                                    <li>
                                      <span>{property.bedrooms ?? 0} <i className="flaticon-bed" /></span>
                                      {t("property.bedrooms")}
                                    </li>
                                    <li>
                                      <span>{property.bathrooms ?? 0} <i className="flaticon-clean" /></span>
                                      {t("property.bathrooms")}
                                    </li>
                                    {property.areaSqft !== null && property.areaSqft !== undefined ? (
                                      <li>
                                        <span>
                                          {property.areaSqft} <i className="flaticon-square-shape-design-interface-tool-symbol" />
                                        </span>
                                        {t("property.squareFt")}
                                      </li>
                                    ) : null}
                                  </ul>
                                </div>
  
                                <div className="product-info-bottom">
                                  <div className="real-estate-agent">
                                    {agentAvatar ? (
                                      <div className="agent-img">
                                        <a href="team-details.html">
                                          <img src={resolvePropertyImage(agentAvatar)} alt={agentName} />
                                        </a>
                                      </div>
                                    ) : null}
                                    <div className="agent-brief">
                                      <h6>
                                        <Link to={`/property/${property.id}`}>{agentName}</Link>
                                      </h6>
                                      <small>{agentRole}</small>
                                    </div>
                                  </div>
  
                                  <div className="product-hover-action">
                                    <ul>
                                      <li ref={shareOpenId === `list-${property.id}` ? shareMenuRef : null}>
                                        <a
                                          href="#"
                                          title="Share"
                                          onClick={(event) => {
                                            event.preventDefault();
                                            setShareOpenId(shareOpenId === `list-${property.id}` ? null : `list-${property.id}`);
                                          }}
                                        >
                                          <i className="fas fa-share-alt" />
                                        </a>
                                        {shareOpenId === `list-${property.id}` && (
                                          <div
                                            className="ltn__social-media-2"
                                            style={{
                                              position: "absolute",
                                              zIndex: 10,
                                              marginTop: "8px",
                                              background: "#fff",
                                              boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                                              borderRadius: "6px",
                                              padding: "8px 12px",
                                            }}
                                          >
                                            <ul style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                              <li>
                                                <a href={getShareLinks(property).whatsapp} target="_blank" rel="noreferrer" title="Share on WhatsApp">
                                                  <i className="fab fa-whatsapp" />
                                                </a>
                                              </li>
                                              <li>
                                                <a href={getShareLinks(property).facebook} target="_blank" rel="noreferrer" title="Share on Facebook">
                                                  <i className="fab fa-facebook-f" />
                                                </a>
                                              </li>
                                              <li>
                                                <a href={getShareLinks(property).instagram} onClick={(event) => handleInstagramShare(event, property)} title="Share on Instagram">
                                                  <i className="fab fa-instagram" />
                                                </a>
                                              </li>
                                            </ul>
                                          </div>
                                        )}
                                      </li>
                                      <li>
                                        <a
                                          href="#"
                                          title="Wishlist"
                                          data-bs-toggle="modal"
                                          data-bs-target="#liton_wishlist_modal"
                                        >
                                          <i className="flaticon-heart-1" />
                                        </a>
                                      </li>
                                      <li>
                                        <Link to={`/property/${property.id}`} title="Product Details">
                                          <i className="flaticon-add" />
                                        </Link>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
  
              <div className="ltn__pagination-area text-center">
                <div className="ltn__pagination">
                  <ul>
                    <li>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage > 1) setCurrentPage(currentPage - 1);
                        }}
                      >
                        <i className="fas fa-angle-double-left" />
                      </a>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li key={page} className={currentPage === page ? "active" : ""}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(page);
                          }}
                        >
                          {page}
                        </a>
                      </li>
                    ))}
                    <li>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                        }}
                      >
                        <i className="fas fa-angle-double-right" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Property;
